import { Button, Chip, faDigits, formatDateShort, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type KardexRow, type StockItemRow, type VoucherRow, type WarehouseRow } from "../../api.js";
import { formatQty, formatRial } from "./format.js";

const KIND_SHORT: Record<VoucherRow["kind"], string> = {
  receipt: "رسید ورود",
  issue: "حواله خروج",
  return_to_supplier: "برگشت خرید",
  waste: "ضایعات",
  count: "انبارگردانی",
  transfer_send: "ارسال انتقال",
  transfer_receive: "دریافت انتقال",
  staff_meal: "مصرف پرسنل",
  "staff-meal": "مصرف پرسنل",
};

export function KardexTab() {
  const warehouses = useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: () => api<WarehouseRow[]>("/inventory/warehouses"),
  });

  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });

  const activeWhs = (warehouses.data ?? []).filter((w) => w.status === "active");
  const activeItems = (items.data ?? []).filter((i) => i.status === "active");

  const [warehouseId, setWarehouseId] = useState<string>("");
  const [itemId, setItemId] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const currentWhId = warehouseId || activeWhs[0]?.id || "";
  const currentItemId = itemId || activeItems[0]?.id || "";

  const queryParams = new URLSearchParams();
  if (fromDate) queryParams.set("from", fromDate);
  if (toDate) queryParams.set("to", toDate);
  const qStr = queryParams.toString() ? `?${queryParams.toString()}` : "";

  const kardex = useQuery({
    queryKey: ["inventory", "kardex", currentWhId, currentItemId, qStr],
    queryFn: () => api<KardexRow[]>(`/inventory/warehouses/${currentWhId}/kardex/${currentItemId}${qStr}`),
    enabled: Boolean(currentWhId && currentItemId),
  });

  const selectedItem = activeItems.find((i) => i.id === currentItemId);
  const selectedWh = activeWhs.find((w) => w.id === currentWhId);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Selector Filters */}
      <div className="row wrap items-center" style={{ gap: "var(--s3)" }}>
        <div className="field" style={{ minWidth: "220px" }}>
          <label htmlFor="kardex-wh">انبار</label>
          <select
            id="kardex-wh"
            className="input"
            value={currentWhId}
            onChange={(e) => {
              setWarehouseId(e.target.value);
            }}
          >
            {activeWhs.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.code})
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ minWidth: "220px" }}>
          <label htmlFor="kardex-item">کالا</label>
          <select
            id="kardex-item"
            className="input"
            value={currentItemId}
            onChange={(e) => {
              setItemId(e.target.value);
            }}
          >
            {activeItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({item.code}) - {item.baseUnit}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ width: "auto" }}>
          <label htmlFor="kardex-from">از تاریخ</label>
          <input
            id="kardex-from"
            type="date"
            className="input"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
            }}
          />
        </div>

        <div className="field" style={{ width: "auto" }}>
          <label htmlFor="kardex-to">تا تاریخ</label>
          <input
            id="kardex-to"
            type="date"
            className="input"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
            }}
          />
        </div>

        {(fromDate || toDate) && (
          <div style={{ alignSelf: "flex-end" }}>
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              پاک کردن تاریخ
            </Button>
          </div>
        )}
      </div>

      <Panel
        title={
          selectedItem && selectedWh
            ? `کاردکس ریالی و مقداری: ${selectedItem.name} در ${selectedWh.name}`
            : "کاردکس کالا"
        }
      >
        {!currentWhId || !currentItemId ? (
          <p className="hint">لطفاً انبار و کالا را برای مشاهده کاردکس انتخاب کنید.</p>
        ) : kardex.isLoading ? (
          <p className="hint">در حال محاسبه گردش کاردکس...</p>
        ) : (kardex.data ?? []).length === 0 ? (
          <p className="hint">هیچ گردشی برای این کالا در انبار انتخابی ثبت نشده است.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th rowSpan={2}>تاریخ</th>
                  <th rowSpan={2}>شماره سند</th>
                  <th rowSpan={2}>نوع تراکنش</th>
                  <th colSpan={2} style={{ textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                    وارده (ورود)
                  </th>
                  <th colSpan={2} style={{ textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                    صادره (خروج)
                  </th>
                  <th colSpan={3} style={{ textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                    مانده
                  </th>
                </tr>
                <tr>
                  <th>مقدار</th>
                  <th>فی وارده</th>
                  <th>مقدار</th>
                  <th>فی صادره</th>
                  <th>موجودی</th>
                  <th>میانگین موزون</th>
                  <th>ارزش کل</th>
                </tr>
              </thead>
              <tbody>
                {(kardex.data ?? []).map((row, idx) => (
                  <tr key={`${row.voucherId}-${String(idx)}`}>
                    <td>{formatDateShort(row.date)}</td>
                    <td className="b">{faDigits(row.voucherNumber)}</td>
                    <td>
                      <Chip plain tone="dark">
                        {KIND_SHORT[row.kind]}
                      </Chip>
                    </td>
                    <td className="num">
                      {row.inQuantityMilli > 0 ? formatQty(row.inQuantityMilli, selectedItem?.baseUnit) : "—"}
                    </td>
                    <td className="num">
                      {row.inQuantityMilli > 0 && row.inUnitPriceRial > 0
                        ? formatRial(row.inUnitPriceRial)
                        : "—"}
                    </td>
                    <td className="num">
                      {row.outQuantityMilli > 0
                        ? formatQty(row.outQuantityMilli, selectedItem?.baseUnit)
                        : "—"}
                    </td>
                    <td className="num">
                      {row.outQuantityMilli > 0 && row.outUnitPriceRial > 0
                        ? formatRial(row.outUnitPriceRial)
                        : "—"}
                    </td>
                    <td className="num b">{formatQty(row.balanceQuantityMilli, selectedItem?.baseUnit)}</td>
                    <td className="num">{formatRial(row.balanceAverageCostRial)}</td>
                    <td className="num b">{formatRial(row.balanceTotalValueRial)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
